import React, { useEffect, useState } from "react";
import { Users, Search, RotateCcw, ChevronUp, ChevronDown, CheckCircle2, XCircle, Info } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

interface CameraUserData {
    id: number;
    username: string;
    user_level: string;
    auth_success: boolean | null;
    created: string;
    [key: string]: any;
}

const CameraUsers: React.FC<{ hideHeader?: boolean; onDataLoaded?: (count: number) => void }> = ({ hideHeader, onDataLoaded }) => {
    const [data, setData] = useState<CameraUserData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === "-") return "-";

        try {
            // Normalize: if no timezone info, assume UTC and add Z
            const normalized =
                /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString)
                    ? dateString
                    : `${dateString}Z`;

            const date = new Date(normalized);

            if (isNaN(date.getTime())) return dateString;

            return date.toLocaleString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
            });
        } catch {
            return dateString;
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const limit = itemsPerPage;

            const params: any = {
                query: JSON.stringify({
                    asset_id: asset_id,
                    client_id: client_id,
                }),
                skip,
                limit,
            };

            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/asset_camera_users", { params });
            if (response.data.status) {
                const total = response.data.total || 0;
                setData(response.data.data || []);
                setTotalItems(total);
                onDataLoaded?.(total);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage, itemsPerPage, sortConfig]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const filteredData = data.filter(item =>
        (item.username?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.user_level?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-4 animate-fade-in">
            {!hideHeader && (
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    <div className="flex items-center">
                        <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                            <Users className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Camera Users</h2>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                className="w-full bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-orange-500 shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => fetchData()}
                            className="p-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors shadow-sm"
                        >
                            <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            )}

            <div className={`bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 overflow-hidden shadow-sm ${hideHeader ? 'rounded-lg' : 'rounded-xl'}`}>
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                                <th className={`text-slate-600 dark:text-gray-200 uppercase tracking-wider cursor-pointer ${hideHeader ? 'px-2 py-1.5 text-[10px]' : 'px-4 py-3 text-xs'} font-semibold`} onClick={() => handleSort('username')}>
                                    <div className="flex items-center gap-1">User {sortConfig?.key === 'username' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}</div>
                                </th>
                                <th className={`text-slate-600 dark:text-gray-200 uppercase tracking-wider cursor-pointer ${hideHeader ? 'px-2 py-1.5 text-[10px]' : 'px-4 py-3 text-xs'} font-semibold`} onClick={() => handleSort('user_level')}>
                                    <div className="flex items-center gap-1">Level {sortConfig?.key === 'user_level' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}</div>
                                </th>
                                <th className={`text-slate-600 dark:text-gray-200 uppercase tracking-wider ${hideHeader ? 'px-2 py-1.5 text-[10px]' : 'px-4 py-3 text-xs'} font-semibold`}>Auth</th>
                                <th className={`text-slate-600 dark:text-gray-200 uppercase tracking-wider ${hideHeader ? 'px-2 py-1.5 text-[10px]' : 'px-4 py-3 text-xs'} font-semibold`}>Created</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="py-10 text-center">
                                        <div className="flex flex-col items-center gap-1">
                                            <RotateCcw className="w-5 h-5 text-orange-500 animate-spin" />
                                            <span className="text-[10px] text-slate-400 dark:text-gray-500 font-bold">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="py-10 text-center text-slate-400 dark:text-gray-500 text-[10px] font-bold uppercase tracking-wider">
                                        No camera users.
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors">
                                        <td className={`font-medium text-gray-900 dark:text-white ${hideHeader ? 'px-2 py-1.5 text-[11px]' : 'px-4 py-3 text-sm'}`}>{item.username || '-'}</td>
                                        <td className={`${hideHeader ? 'px-2 py-1.5' : 'px-4 py-3'}`}>
                                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                                                {item.user_level || 'unknown'}
                                            </span>
                                        </td>
                                        <td className={`${hideHeader ? 'px-2 py-1.5' : 'px-4 py-3'}`}>
                                            {item.auth_success === null ? (
                                                <div className="flex items-center gap-1 text-slate-500 dark:text-gray-100">
                                                    <Info className="w-3 h-3" />
                                                    <span className="text-[9px] uppercase font-bold">Pending</span>
                                                </div>
                                            ) : item.auth_success ? (
                                                <div className="flex items-center gap-1 text-green-600 dark:text-green-500">
                                                    <CheckCircle2 className="w-3 h-3" />
                                                    <span className="text-[9px] uppercase font-bold">Success</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1 text-red-600 dark:text-red-500">
                                                    <XCircle className="w-3 h-3" />
                                                    <span className="text-[9px] uppercase font-bold">Failed</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className={`text-slate-600 dark:text-gray-200 font-mono ${hideHeader ? 'px-2 py-1.5 text-[10px]' : 'px-4 py-3 text-sm'}`}>
                                            {formatDate(item.created)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className={`bg-slate-50 dark:bg-gray-900/50 border-t border-slate-200 dark:border-gray-700 ${hideHeader ? 'px-3 py-1.5' : 'px-6 py-4'}`}>
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] text-slate-500 dark:text-gray-400 font-medium tracking-tight">
                                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="p-1 text-slate-500 dark:text-white hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &laquo;
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-1 text-slate-500 dark:text-white hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &lsaquo;
                            </button>
                            <div className="bg-orange-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1 text-slate-500 dark:text-white hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &rsaquo;
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1 text-slate-500 dark:text-white hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &raquo;
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CameraUsers;
